/**
 * Axios response interceptor — impersonation grant-expired short-circuit
 * (#1137 Task 3.3).
 *
 * When the backend rejects a request with 401/403 and a
 * `detail.code` of `impersonation_grant_expired` or
 * `impersonation_target_left_workspace`, the actor's auth is fine —
 * the *grant* is what's gone. Running the auth-refresh branch in that
 * case would burn a refresh attempt, blow the actor's session, and
 * bounce them to /login instead of showing the explicit "Impersonation
 * session expired" UX.
 *
 * This handler runs BEFORE the auth-refresh logic in App.js. It clears
 * the sessionStorage grant, alerts the user, and bounces back to the
 * origin admin path (or a sensible fallback per scope).
 *
 * Returns `true` when the error was handled — the caller should
 * `Promise.reject(error)` and skip the auth-refresh branch. Returns
 * `false` for any other error so the existing refresh path runs
 * unchanged.
 *
 * Bare function exported so unit tests can drive it without spinning up
 * axios or React.
 */

const STORAGE_KEY = 'fray_impersonation';

// Backend codes that mean "your impersonation grant is dead or unusable here."
// Some surface as 401 (consumer collapses revoked-grants and disabled-actors
// into the same code), some as 403 (workspace boundary / actor admin loss /
// cross-workspace escalation block). All three 403 cases mean the actor's
// own auth is fine — the *grant* can't authorize this request — so the
// interceptor must short-circuit BEFORE the auth-refresh path so we don't
// burn a refresh attempt and bounce to /login.
const EXPIRED_CODES = new Set([
  'impersonation_grant_expired',
  'impersonation_target_left_workspace',
  // #1137 NB1: cross-workspace escalation block — actor held a workspace
  // grant but tried to act in a different workspace.
  'impersonation_cross_workspace',
  // #1137 N4: actor lost workspace-admin role between mint and consume.
  'impersonation_actor_lost_admin',
]);

export function handleGrantExpired(error) {
  const status = error?.response?.status;
  const code = error?.response?.data?.detail?.code;

  if (status !== 401 && status !== 403) return false;
  if (!EXPIRED_CODES.has(code)) return false;

  // Defensive: if no context to clear (shouldn't normally happen — backend
  // wouldn't emit this code without an active grant), still skip refresh
  // since the actor's auth isn't the failing leg.
  let raw = null;
  try { raw = sessionStorage.getItem(STORAGE_KEY); } catch (_) { raw = null; }

  if (raw) {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* storage gone */ }
    let ctx = {};
    try { ctx = JSON.parse(raw) || {}; } catch (_) { ctx = {}; }

    const fallback = ctx.scope === 'workspace'
      ? `/enterprise-admin/${ctx.workspace_id || ''}`
      : '/admin';
    const back = ctx.origin_path || fallback;

    try { window.alert('Impersonation session expired.'); } catch (_) { /* non-browser env */ }
    try { window.location.href = back; } catch (_) { /* navigation suppressed in tests */ }
  }

  return true;
}
